import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import { useImageDatasetActions } from '~/hooks/dataset/useImageDatasetActions';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import { useReformatImage } from '../useReformatImage';
import { REFORMAT_BLOCKED_MESSAGE_IDS } from './reformatBlockedMessages';
import { useReformatBlockedReason } from './useReformatBlockedReason';

type Role = 'primary' | 'secondary' | null;

const ImageCard = React.memo(function ImageCard({
    image,
    role,
    isSelected,
    onSelect,
    onReformat,
    onRename,
    onDelete,
    onDownload,
}: {
    image: AvailableImage;
    role: Role;
    isSelected?: boolean;
    onSelect?: () => void;
    onReformat?: (img: AvailableImage) => void;
    onRename?: (img: AvailableImage) => void;
    onDelete?: (img: AvailableImage) => void;
    onDownload?: (img: AvailableImage) => void;
}) {
    const intl = useIntl();
    const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

    const isReformat = !!image.needsReformat;
    const showReformat = isSelected && onReformat;

    // Mirror of the domain predicate: the control names why it is refused
    // rather than letting the reformat start and fail on the first read.
    // `reformatBlockedReason` covers the progressively loaded image too, so
    // an image whose slices are still arriving is refused here and in the
    // dialog by the same test.
    const blockedReason = useReformatBlockedReason(image);
    const reformatTooltip = blockedReason
        ? t(REFORMAT_BLOCKED_MESSAGE_IDS[blockedReason])
        : t('reformatTooltip');
    // Exporting a partly arrived image would write a truncated series to
    // disk under the source's identity. `Image3D.getSlicesAsDicomBlobs`
    // throws on it; the disabled control carries the reason instead.
    const imageGate = useImageCompletenessGate(image.imageObject);

    const handleReformatClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onReformat?.(image);
        },
        [onReformat, image]
    );

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: 'relative',
                cursor: onSelect ? 'pointer' : 'default',
                bgcolor: isSelected
                    ? 'rgba(144, 202, 249, 0.08)'
                    : 'transparent',
                '&:hover': onSelect
                    ? {
                          bgcolor: isSelected
                              ? 'rgba(144, 202, 249, 0.12)'
                              : 'rgba(144, 202, 249, 0.06)',
                      }
                    : undefined,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                    zIndex: 1,
                },
                transition: 'all 0.15s ease-in-out',
            }}
        >
            <AvailableImageItem
                image={image}
                isSecondary={role === 'secondary'}
                isSelected={false}
                onReformat={onReformat ? () => onReformat(image) : undefined}
                onDownload={
                    onDownload && !imageGate.isBlocked
                        ? () => onDownload(image)
                        : undefined
                }
                onRename={onRename ? () => onRename(image) : undefined}
                onDelete={onDelete ? () => onDelete(image) : undefined}
            />

            {showReformat && (
                <Box
                    display="flex"
                    gap={0.5}
                    px={2}
                    pb={0.75}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {isReformat && (
                        <Tooltip title={reformatTooltip} placement="right">
                            {/* A disabled MUI Button emits no pointer events,
                                so the tooltip that says WHY needs a wrapper
                                that still does. */}
                            <span>
                                <Button
                                    data-testid="reformat-image-button"
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    onClick={handleReformatClick}
                                    disabled={blockedReason !== null}
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    <FormattedMessage id="reformatImage" />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {!isReformat && image.imageObject && (
                        <Tooltip title={reformatTooltip} placement="right">
                            <span>
                                <Button
                                    data-testid="reformat-loaded-image-button"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleReformatClick}
                                    disabled={blockedReason !== null}
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    <FormattedMessage id="reformatLoadedImage" />
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default function ImageList() {
    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);
    const { selectedSecondaryImage } = useSecondaryImage();
    const actions = useImageDatasetActions();
    const { openReformat, reformatDialog } = useReformatImage();

    const getRole = useCallback(
        (image: AvailableImage): Role => {
            if (image.seriesInstanceUID === primaryImage?.seriesUid) {
                return 'primary';
            }
            if (
                image.imageObject?.dicomUid === selectedSecondaryImage?.dicomUid
            ) {
                return 'secondary';
            }
            return null;
        },
        [primaryImage?.seriesUid, selectedSecondaryImage?.dicomUid]
    );

    const handleImageClick = useCallback(
        (image: AvailableImage) => {
            handleChangePrimaryImage(image);
        },
        [handleChangePrimaryImage]
    );

    if (availableImages.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={2} py={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="images" />
                    </Typography>
                </Box>
                <Box>
                    {availableImages.map((img, index) => {
                        const role = getRole(img);
                        const isPrimary = role === 'primary';
                        return (
                            <React.Fragment key={img.seriesInstanceUID}>
                                {index > 0 && <Divider />}
                                <ImageCard
                                    image={img}
                                    role={role}
                                    isSelected={isPrimary}
                                    onSelect={
                                        !img.needsReformat
                                            ? () => handleImageClick(img)
                                            : undefined
                                    }
                                    onReformat={openReformat}
                                    onRename={
                                        actions.canRename(img)
                                            ? actions.rename
                                            : undefined
                                    }
                                    onDelete={
                                        actions.canRemove(img)
                                            ? actions.remove
                                            : undefined
                                    }
                                    onDownload={
                                        !img.imageObject && !img.rawSeries
                                            ? handleImageClick
                                            : undefined
                                    }
                                />
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>

            {actions.dialogs}

            {reformatDialog}
        </>
    );
}
